import { useTranslation } from 'react-i18next';
import type { ILocationDrillDown, ILocationProductivityRow } from '@interfaces/IProductivity';
import { Drawer } from '@components/ui/Drawer/Drawer';
import { ShieldIcon } from '@utils/icons';
import { ConfidencePill } from '../Table/ConfidencePill/ConfidencePill';
import { DrillDownFigures } from './DrillDownFigures/DrillDownFigures';
import { DrillDownProfessions } from './DrillDownProfessions/DrillDownProfessions';
import { DrillDownFooter } from './DrillDownFooter/DrillDownFooter';
import drawerText from '@components/ui/Drawer/drawerContent.module.css';
import styles from './LocationDrillDownDrawer.module.css';

interface ILocationDrillDownDrawerProps {
  row: ILocationProductivityRow;
  /** The floor's figures split by profession. Optional — a floor whose
   *  drill-down has not arrived still opens on its own figures. */
  drillDown?: ILocationDrillDown;
  /** Kept mounted and flipped, so the shared drawer plays its slide-out. */
  open: boolean;
  onClose: () => void;
}

/** A floor's detail on the app's drawer: the row's figures as tiles, then
 *  the professions that worked the floor in the timeframe. */
export function LocationDrillDownDrawer({ row, drillDown, open, onClose }: ILocationDrillDownDrawerProps) {
  const { t } = useTranslation();
  return (
    <Drawer
      open={open}
      onClose={onClose}
      ariaLabel={row.location}
      closeLabel={t('common.close')}
      panelClassName={styles.panel}
    >
      <header className={drawerText.head}>
        <span className={drawerText.eyebrow}>{t('productivity.drillDown.eyebrow')}</span>
        <h2 className={drawerText.title}>{row.location}</h2>
        {row.status && <span className={styles.status}>{t(`productivity.status.${row.status}`)}</span>}
      </header>

      <DrillDownFigures row={row} />

      <div className={styles.confidenceRow}>
        <span className={styles.confidenceLabel}>
          <ShieldIcon size={14} />
          {t('productivity.drillDown.dataConfidence')}
        </span>
        <ConfidencePill level={row.confidence} reason={row.confidenceReason} />
      </div>

      <section className={styles.section}>
        <h3 className={drawerText.sectionTitle}>{t('productivity.drillDown.professionsTitle')}</h3>
        <DrillDownProfessions professions={drillDown?.professions ?? []} />
      </section>

      <DrillDownFooter />
    </Drawer>
  );
}
